import { ChevronLeft, Sun, Moon, LogOut, Mail } from 'lucide-react';
import type { ConnectionStatus, ConnectionState } from '@deckspace/types';

/** Map connection state to semantic color token. */
function getStatusColor(state: ConnectionState): string {
  switch (state) {
    case 'connected':
      return 'bg-success';
    case 'connecting':
    case 'waiting':
      return 'bg-warning';
    case 'error':
    case 'disconnected':
      return 'bg-destructive';
    default:
      return 'bg-muted';
  }
}

/** Map connection state to display text. */
function getStatusText(status: ConnectionStatus): string {
  switch (status.state) {
    case 'connected':
      return status.port ? `Connected (port ${status.port})` : 'Connected';
    case 'connecting':
      return 'Connecting...';
    case 'waiting':
      return 'Waiting for MCP server...';
    case 'error':
      return status.error || 'Connection error';
    case 'disconnected':
      return 'Disconnected';
    default:
      return status.state;
  }
}

/**
 * Props for the SettingsPage component
 */
export interface SettingsPageProps {
  /** Current color scheme */
  colorScheme: 'dark' | 'light';
  /** Change the color scheme */
  onColorSchemeChange: (scheme: 'dark' | 'light') => void;
  /** Connection status information */
  connectionStatus: ConnectionStatus;
  /** App version string */
  version: string;
  /** Close settings and return to main view */
  onClose: () => void;
  /** Signed-in user's email address */
  userEmail?: string | null;
  /** Sign out handler */
  onSignOut?: () => void;
}

/**
 * Settings page displaying theme preferences, connection status, and app information.
 *
 * @example
 * ```tsx
 * <SettingsPage
 *   colorScheme={theme}
 *   onColorSchemeChange={setTheme}
 *   connectionStatus={{ state: 'connected', port: 45678 }}
 *   version="0.1.0"
 *   onClose={() => setShowSettings(false)}
 * />
 * ```
 */
export function SettingsPage({
  colorScheme,
  onColorSchemeChange,
  connectionStatus,
  version,
  onClose,
  userEmail,
  onSignOut,
}: SettingsPageProps) {
  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header with back button */}
        <div className="flex items-center gap-3 mb-8">
          <button
            onClick={onClose}
            className="p-2 hover:bg-card rounded-md transition-colors text-muted-foreground hover:text-foreground"
            title="Back to workspaces"
          >
            <ChevronLeft size={20} />
          </button>
          <h1 className="text-xl font-semibold text-foreground">Settings</h1>
        </div>

        {/* Settings sections */}
        <div className="space-y-8">
          {/* Account */}
          {(userEmail || onSignOut) && (
            <section>
              <h2 className="text-sm font-medium text-foreground mb-3">Account</h2>
              <div className="bg-card border border-border rounded-lg p-4 space-y-4">
                {userEmail && (
                  <div className="flex items-center gap-3">
                    <Mail size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{userEmail}</span>
                  </div>
                )}
                {onSignOut && (
                  <button
                    onClick={onSignOut}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border
                      text-sm text-foreground hover:bg-background transition-colors"
                  >
                    <LogOut size={16} />
                    Sign out
                  </button>
                )}
              </div>
            </section>
          )}

          {/* Appearance */}
          <section>
            <h2 className="text-sm font-medium text-foreground mb-3">Appearance</h2>
            <div className="flex gap-2">
              <button
                onClick={() => onColorSchemeChange('light')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  colorScheme === 'light'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card border-border text-foreground hover:bg-background'
                }`}
              >
                <Sun size={16} />
                Light
              </button>
              <button
                onClick={() => onColorSchemeChange('dark')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  colorScheme === 'dark'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card border-border text-foreground hover:bg-background'
                }`}
              >
                <Moon size={16} />
                Dark
              </button>
            </div>
          </section>

          {/* Connection Status */}
          <section>
            <h2 className="text-sm font-medium text-foreground mb-3">Connection Status</h2>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                {/* Status indicator dot */}
                <div className={`w-3 h-3 rounded-full ${getStatusColor(connectionStatus.state)}`} />
                <span className="text-sm text-foreground">{getStatusText(connectionStatus)}</span>
              </div>
              {connectionStatus.error && connectionStatus.state === 'error' && (
                <p className="mt-2 text-sm text-destructive">{connectionStatus.error}</p>
              )}
            </div>
          </section>

          {/* About */}
          <section>
            <h2 className="text-sm font-medium text-foreground mb-3">About</h2>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Application</span>
                  <span className="text-sm text-foreground">Deckspace</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Version</span>
                  <span className="text-sm text-foreground">{version}</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
